/**
 * JorisDashboard — privé sturings-dashboard voor joris@fullframe.nl.
 *
 * Doel: in één oogopslag zien hoe druk het team is, met expliciete
 * sturing-signalen voor PLs, regisseurs en cameramannen.
 *
 * V1 (deze versie):
 *  - Highlights: auto-gegenereerde tekst-signalen
 *  - PL-workload: projecten per PL, breakdown per status, taken/deliverables/draaidagen
 *  - Crew-load: regisseurs en cameramannen draaidagen-overzicht (4 wks vooruit)
 *
 * V2+ (later): €waarde, history, editors/animators task-load, PL-agenda
 *
 * Hardcoded gate: alleen joris@fullframe.nl. Niet via permissions-DB
 * omdat dit *expliciet* een persoonlijk dashboard is, geen rolfeature.
 */
import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Typography,
  Paper,
  Stack,
  Chip,
  CircularProgress,
  Alert,
  IconButton,
  Tooltip,
  Divider,
  Collapse,
  Tabs,
  Tab,
  Switch,
  FormControlLabel,
  Checkbox,
} from "@mui/material";
import { Navigate } from "react-router-dom";
import RefreshIcon from "@mui/icons-material/Refresh";
import PersonIcon from "@mui/icons-material/Person";
import VideocamIcon from "@mui/icons-material/Videocam";
import MovieFilterIcon from "@mui/icons-material/MovieFilter";
import AssignmentIcon from "@mui/icons-material/Assignment";
import LocalMoviesIcon from "@mui/icons-material/LocalMovies";
import LightbulbIcon from "@mui/icons-material/Lightbulb";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import HistoryIcon from "@mui/icons-material/History";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import TrendingDownIcon from "@mui/icons-material/TrendingDown";
import TrendingFlatIcon from "@mui/icons-material/TrendingFlat";
import EventIcon from "@mui/icons-material/Event";
import GroupsIcon from "@mui/icons-material/Groups";
import CloseIcon from "@mui/icons-material/Close";
import { TextField, MenuItem, Dialog as MuiDialog, Button } from "@mui/material";

import { authFetch } from "../../utils/authHelpers";
import { BACKEND_URL, AI_ENGINE_URL } from "../../utils/apiConfig";
import PageHeader from "../../ui-components/PageHeader";
import DeclarabiliteitBlok from "./DeclarabiliteitBlok";
import { useUserRole } from "../../contexts/UserRoleContext";
import { formatDateISO } from '../../utils/weekUtils';

// ── Gating ───────────────────────────────────────────────────────────────────
// MT-sturingsdashboard: zichtbaar voor management/admin (kpi_dashboard-permissie).
// Primaire gate zit op de route (ProtectedRoute); deze check is defense-in-depth.

// ── Tunables ────────────────────────────────────────────────────────────────
// Senior-camera-flag: lagere target (max 1/wk). Hardcoded want dit is
// een team-keuze, niet iets uit Airtable.
const SENIOR_CAMERAMEN_EMAILS = new Set([
  "ton@full-frame.nl",
  "pepijn@full-frame.nl",
]);

// "Freelancer" is een generieke placeholder-persoon in Airtable die gebruikt
// wordt voor incidentele freelance crew (sammie, externe camera-operators etc).
// Forceer als cameraman zodat deze bookings op de Camera-tab verschijnen.
const FORCED_CAMERAMEN_EMAILS = new Set([
  "freelance@fullframe.nl",
]);

// Task-status/types die NIET als productie-werkdruk meetellen voor uitvoerders.
// Categorieën uit CLAUDE.md, plus enkele observaties uit live data:
//   - Afspraak / Vrij/intern: meetings of vrij — geen productie
//   - Draaidag: camera-dag, al gerekend in Crew-bezetting (dubbel tellen voorkomen)
//   - Vanallesuurtje: administratief blok, ~1u/dag standaard
//   - Afgerond / Opgestuurd / Gearchiveerd: al klaar/gedaan
const NON_PRODUCTION_TASK_STATUSES = new Set([
  "Afspraak",
  "Vrij/intern",
  "Draaidag",
  "Vanallesuurtje",
  "Afgerond",
  "Opgestuurd",
  "Gearchiveerd",
]);
const isProductionTask = (t) =>
  !NON_PRODUCTION_TASK_STATUSES.has(t.status) &&
  !NON_PRODUCTION_TASK_STATUSES.has(t.type);

// Targets per role per week (voor sturing-signalen)
const TARGETS = {
  regisseur: { ideal: [1, 2], unit: "draaidagen", label: "Regisseur" },
  cameraman: { ideal: [2, 2], unit: "draaidagen", label: "Cameraman" },
  cameramanSenior: { ideal: [0, 1], unit: "draaidagen", label: "Senior camera" },
};

// Project-status indeling — afgeleid uit Airtable Status
const ACTIVE_STATUSES = ["In progress", "Lead", "Offerte"];
const OPDRACHT_STATUSES = ["In progress"];               // bevestigde opdrachten
const OFFERTE_STATUSES = ["Lead", "Offerte"];            // potentieel werk, nog niet zeker

// ── Helpers ──────────────────────────────────────────────────────────────────

function startOfWeek(d) {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  const day = x.getDay();
  const diff = (day === 0 ? -6 : 1 - day); // ma start
  x.setDate(x.getDate() + diff);
  return x;
}

function weekKey(d) {
  const wkStart = startOfWeek(d);
  return wkStart.toISOString().slice(0, 10);
}

function fmtEuro(n) {
  if (n == null || n === 0) return "—";
  if (n >= 1000000) return `€${(n / 1000000).toFixed(1)}M`;
  if (n >= 1000) return `€${Math.round(n / 1000)}k`;
  return `€${Math.round(n)}`;
}

function fmtWeekLabel(weekStart, today = new Date()) {
  const tWk = startOfWeek(today);
  const diffWeeks = Math.round((weekStart - tWk) / (7 * 86400000));
  if (diffWeeks === 0) return "Deze week";
  if (diffWeeks === 1) return "Vol. week";
  if (diffWeeks === -1) return "Vorige week";
  if (diffWeeks > 0) return `Wk +${diffWeeks}`;
  return `Wk ${diffWeeks}`;
}

function fmtWeekDateRange(weekStart) {
  const start = new Date(weekStart);
  const end = new Date(weekStart);
  end.setDate(end.getDate() + 6);
  const fmt = (d, includeMonth) => {
    const day = d.getDate();
    const month = d.toLocaleDateString("nl-NL", { month: "short" });
    return includeMonth ? `${day} ${month}` : `${day}`;
  };
  // Als zelfde maand: "5-11 mei". Anders: "29 apr - 5 mei"
  if (start.getMonth() === end.getMonth()) {
    return `${start.getDate()}-${end.getDate()} ${end.toLocaleDateString("nl-NL", { month: "short" })}`;
  }
  return `${fmt(start, true)} - ${fmt(end, true)}`;
}

// Detecteer Airtable record ID ("recXXXXXXXXXXXXXX" — 17 chars, prefix "rec")
function looksLikeAirtableId(s) {
  return typeof s === "string" && /^rec[A-Za-z0-9]{14}$/.test(s);
}

function pickFunctie(functieField) {
  // Functie kan string óf array zijn (Airtable multiselect / linked)
  if (!functieField) return [];
  if (Array.isArray(functieField)) return functieField.map(String);
  if (typeof functieField === "string") return functieField.split(",").map((s) => s.trim()).filter(Boolean);
  return [];
}

function isCameraman(functies) {
  return functies.some((f) => /camera/i.test(f));
}
function isRegisseur(functies) {
  return functies.some((f) => /regisseur/i.test(f));
}
function isProjectleider(functies) {
  return functies.some((f) => /projectleider/i.test(f));
}

// ── Page ─────────────────────────────────────────────────────────────────────

export default function JorisDashboard() {
  const { permissions } = useUserRole();

  if (!permissions?.kpi_dashboard?.view) {
    return <Navigate to="/" replace />;
  }

  return <DashboardBody />;
}

// ── Body ─────────────────────────────────────────────────────────────────────

function DashboardBody() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [projects, setProjects] = useState([]);
  const [draaidagen, setDraaidagen] = useState([]);
  const [personen, setPersonen] = useState([]);
  const [refreshKey, setRefreshKey] = useState(0);
  const [debugMode, setDebugMode] = useState(false);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      setError(null);
      try {
        // Vanaf maandag deze week, zodat earlier-this-week draaidagen ook tellen
        const mondayStart = formatDateISO(startOfWeek(new Date())); // FE-07: toISOString gaf de zondag
        const [pRes, dRes, persRes] = await Promise.all([
          // PL-workload uit Gripp (waarheid, gegroepeerd op starred projectleider) i.p.v.
          // de stale Airtable-mirror. Geeft dezelfde record-shape terug als /Api/projects.
          authFetch(`${BACKEND_URL}/Api/gripp/pl-workload`),
          // /Api/draaidagen is PG-backed en filtert al op all_day=true, hidden=false,
          // is_valid=true. Dat sluit Outlook-meetings/partial events automatisch uit.
          authFetch(`${BACKEND_URL}/Api/draaidagen?startDate=${mondayStart}`),
          // /planner-todo/persons leest rechtstreeks uit Airtable werknemers view
          // en geeft clean function + type velden (Medewerker/Freelancer/Stagiair)
          authFetch(`${BACKEND_URL}/Api/planner-todo/persons`),
        ]);
        if (cancelled) return;
        if (!pRes.ok) throw new Error(`Projecten: HTTP ${pRes.status}`);
        if (!dRes.ok) throw new Error(`Draaidagen: HTTP ${dRes.status}`);
        if (!persRes.ok) throw new Error(`Personen: HTTP ${persRes.status}`);

        const pJson = await pRes.json();
        const dJson = await dRes.json();
        const persJson = await persRes.json();

        setProjects(pJson?.projects || []);
        // /Api/draaidagen returnt array van Airtable-records [{ id, fields: {...} }].
        // Map naar de "flat" shape die buildCrewLoad verwacht (compat met /upcoming).
        // Filter: vanaf maandag-deze-week tot 4 weken vooruit (zodat eerder-deze-week
        // draaidagen óók meetellen). Alleen entries met een gelinkt project.
        const nowDate = new Date();
        const monday = startOfWeek(nowDate);
        const startMs = monday.getTime();
        const endMs = startMs + 5 * 7 * 86400000; // huidige week + 4 vooruit
        const rawList = Array.isArray(dJson) ? dJson : (dJson?.draaidagen || []);
        const flatten = (rec) => {
          const f = rec.fields || {};
          // Skip records waarvan de Outlook-link verbroken is (FOUT-status in
          // Airtable Statussymbool = draaidag is gecanceld / niet doorgegaan).
          // Backend filtert hier ook al op, dit is defense-in-depth.
          if (f.outlook_link_status === false) return null;
          // Gebruik de "pure" datum (YYYY-MM-DD) als die er is — dat voorkomt
          // timezone-shifts (StartdatumOutlook is UTC datetime, kan dag schuiven).
          const dateStr = f.StartDatum || f.StartdatumOutlook || null;
          const ddMs = dateStr ? Date.parse(dateStr) : NaN;
          if (!Number.isFinite(ddMs) || ddMs < startMs || ddMs > endMs) return null;
          // Project-link is administratief (kan nog niet gekoppeld zijn), niet
          // bepalend voor of dit een draaidag is. Niet filteren.
          const projectnaam = (f["Projectnaam (from Project)"] || [])[0] || "";
          const projectnummer = (f["Projectnummer (from Project)"] || [])[0] || "";

          const eindDatum = f.EindDatum || f.EinddatumOutlook || dateStr;
          // Bereken aantal kalenderdagen (multi-day draaidagen tellen N keer)
          const startD = new Date(dateStr);
          startD.setHours(0, 0, 0, 0);
          const endD = new Date(eindDatum);
          endD.setHours(0, 0, 0, 0);
          const days = Math.max(1, Math.round((endD - startD) / 86400000) + 1);

          const naam = f.Draaidag || "";
          const isOptie = /^\s*OPTIE/i.test(naam);

          return {
            id: rec.id,
            naam,
            startDatum: dateStr,
            eindDatum,
            days,
            isOptie,
            locatie: f.Locatie || "",
            project: f.Project || [],
            projectnummer,
            projectnaam,
            klantnaam: (f["KlantnaamString (from Project)"] || [])[0] || "",
            crew: [],
            crewNaam: f["Naam (from Crew)"] || [],
            crewEmail: [],
          };
        };
        const flat = rawList.map(flatten).filter(Boolean);
        setDraaidagen(flat);
        // /Api/planner-todo/persons returnt { persons: [{ id, name, firstName, email, type, function }] }
        const persData = persJson?.persons || persJson?.people || persJson?.records || [];
        setPersonen(Array.isArray(persData) ? persData : []);
      } catch (e) {
        if (!cancelled) setError(e.message || "Kon dashboard-data niet laden.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, [refreshKey]);

  // ── Compute derived data ──
  const plWorkload = useMemo(() => buildPlWorkload(projects), [projects]);
  const crewLoad = useMemo(() => buildCrewLoad(draaidagen, personen), [draaidagen, personen]);
  const highlights = useMemo(() => buildHighlights(plWorkload, crewLoad), [plWorkload, crewLoad]);

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", py: 12 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ width: "100%", maxWidth: 1800, mx: "auto", px: { xs: 2, sm: 3, md: 5 }, pt: { xs: 3, md: 5 }, pb: 6 }}>
      <PageHeader
        icon="dashboard"
        title="Werkdruk PL's & team"
        subtitle="Projecten per PL, draaidagen van regie en camera, uren van uitvoerders"
        accent={{ bg: "#fef3c7", color: "#92400e" }}
        rightActions={(
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
            <FormControlLabel
              control={
                <Switch
                  size="small"
                  checked={debugMode}
                  onChange={(e) => setDebugMode(e.target.checked)}
                />
              }
              label={<Typography variant="caption" sx={{ fontFamily: "monospace" }}>Debug</Typography>}
              sx={{ mr: 0 }}
            />
            <Tooltip title="Vernieuwen">
              <IconButton onClick={() => setRefreshKey((k) => k + 1)}>
                <RefreshIcon />
              </IconButton>
            </Tooltip>
          </Box>
        )}
      />
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <KpiTotalsSection refreshKey={refreshKey} draaidagen={draaidagen} debugMode={debugMode} />
      <DeclarabiliteitBlok refreshKey={refreshKey} />
      <HighlightsSection highlights={highlights} />
      <CrewLoadSection crewLoad={crewLoad} debugMode={debugMode} />
      <PLWorkloadSection plWorkload={plWorkload} />
      <UitvoerderLoadSection personen={personen} refreshKey={refreshKey} />
      <HistorySection />
    </Box>
  );
}

// ── Highlights ───────────────────────────────────────────────────────────────

function buildHighlights(plWorkload, crewLoad) {
  const out = [];
  const today = new Date();

  // Bouw 4 weken: deze + vol + +2 + +3
  const weeks = [];
  for (let i = 0; i < 4; i++) {
    const wkStart = startOfWeek(today);
    wkStart.setDate(wkStart.getDate() + i * 7);
    weeks.push({
      key: weekKey(wkStart),
      label: i === 0 ? "deze week" : i === 1 ? "vol. week" : `wk +${i}`,
    });
  }

  // Per week: verzamel boven-target en under-target counts
  const fmtPerson = (person, total, opties) => {
    const optStr = opties > 0 ? ` (+${opties} opt)` : "";
    return `${person.naam.split(/\s+/)[0]} ${total}${optStr}`;
  };

  const isCameraRole = (r) => r === "cameraman" || r === "cameramanSenior";

  // 1. WARNINGS — per week één regel met alle namen boven target
  for (let i = 0; i < weeks.length; i++) {
    const wk = weeks[i];
    const aboveTarget = [];
    for (const person of crewLoad) {
      const cell = person.byWeek[wk.key] || { confirmed: 0, opties: 0 };
      const total = (cell.confirmed || 0) + (cell.opties || 0);
      const target = person.target;
      if (!target) continue;
      const [, hi] = target.ideal;
      if (total > hi) {
        aboveTarget.push({ person, total, opties: cell.opties || 0, role: person.role });
      }
    }
    if (aboveTarget.length > 0) {
      aboveTarget.sort((a, b) => b.total - a.total);
      const names = aboveTarget.map((s) => fmtPerson(s.person, s.total, s.opties)).join(", ");
      out.push({
        kind: "warn",
        text: `⚠️ ${wk.label.charAt(0).toUpperCase() + wk.label.slice(1)}: ${names} — boven target.`,
      });
    }
  }

  // 2. CAPACITEIT — gemiddelde per rol over 4 weken (camera + regie apart)
  // Toon alleen als gemiddelde structureel onder target zit (= ruimte)
  const roleSummary = (filterFn, label, emoji, target) => {
    const persons = crewLoad.filter(filterFn);
    if (persons.length === 0) return null;
    let totalSum = 0, weeks4Persons = 0;
    for (const p of persons) {
      for (const wk of weeks) {
        const cell = p.byWeek[wk.key] || { confirmed: 0, opties: 0 };
        totalSum += (cell.confirmed || 0) + (cell.opties || 0);
        weeks4Persons++;
      }
    }
    const avg = weeks4Persons > 0 ? totalSum / weeks4Persons : 0;
    if (avg < target) {
      return {
        kind: "low",
        text: `${emoji} ${label} komende 4 wkn: gem ${avg.toFixed(1)} dd/wk (target ${target}) — ruimte.`,
      };
    }
    return null;
  };

  const cameraSummary = roleSummary(
    (p) => isCameraRole(p.role) && p.role !== "cameramanSenior",
    "Camera-team", "🎥", 2
  );
  if (cameraSummary) out.push(cameraSummary);

  const regieSummary = roleSummary((p) => p.role === "regisseur", "Regie-team", "🎬", 1.5);
  if (regieSummary) out.push(regieSummary);

  // 3. PL — drukste PL met opdrachten-waarde
  if (plWorkload.length > 0) {
    const top = plWorkload[0];
    const valueStr = top.opdrachtenValue ? ` t.w.v. €${Math.round(top.opdrachtenValue / 1000)}k` : "";
    out.push({
      kind: "info",
      text: `📋 ${top.naam} leidend met ${top.opdrachtenCount || 0} opdrachten${valueStr}` +
            ((top.offertesCount || 0) > 0 ? ` + ${top.offertesCount} offertes` : "") + ".",
    });
  }

  // 4. Geen warnings én geen ruimte-summaries? Dan stilte = goed nieuws
  if (out.length === 0 || (out.length === 1 && out[0].kind === "info")) {
    out.unshift({
      kind: "low",
      text: "✓ Geen urgente sturing nodig — crew on target komende 4 weken.",
    });
  }

  return out;
}

function HighlightsSection({ highlights }) {
  if (highlights.length === 0) return null;
  return (
    <Paper sx={{
      p: 2.5, mb: 3, borderRadius: 3,
      bgcolor: "#fefce8", border: "1px solid #fde68a",
    }}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1.5 }}>
        <LightbulbIcon sx={{ color: "#92400e" }} />
        <Typography variant="subtitle1" sx={{ fontWeight: 700, color: "#92400e" }}>
          Highlights & sturing-signalen
        </Typography>
      </Stack>
      <Stack spacing={0.75}>
        {highlights.map((h, i) => (
          <Typography
            key={i}
            variant="body2"
            sx={{
              color: h.kind === "warn" ? "#b91c1c" : h.kind === "low" ? "#0369a1" : "#374151",
              fontWeight: h.kind === "warn" ? 600 : 400,
            }}
          >
            {h.text}
          </Typography>
        ))}
      </Stack>
    </Paper>
  );
}

// ── PL Workload ──────────────────────────────────────────────────────────────

function buildPlWorkload(projects) {
  const byPL = new Map();
  for (const p of projects) {
    const f = p.fields || p;
    const plNames = f["Projectleider (Volledige naam)"] || [];
    const plArr = Array.isArray(plNames) ? plNames : [plNames];

    for (const pl of plArr) {
      if (!pl) continue;
      if (!byPL.has(pl)) {
        byPL.set(pl, {
          naam: pl,
          projects: [],
          totalProjects: 0,
          activeProjects: 0,
          byStatus: {},
          totalTaken: 0,
          totalDeliverables: 0,
          totalDraaidagen: 0,
        });
      }
      const entry = byPL.get(pl);
      // OpdrachtwaardeExclBTW > Prijs Rollup als fallback (zelfde logica als RevenueForecast)
      const value = Number(f.OpdrachtwaardeExclBTW || f["Prijs Rollup (from OfferteRegels)"] || 0);
      entry.projects.push({
        nummer: f.Projectnummer || "",
        naam: f.Projectnaam || "",
        status: f.Status || "Onbekend",
        klant: f.KlantnaamString || "",
        taken: f["Aantal taken"] || 0,
        deliverables: f["Aantal deliverables"] || 0,
        draaidagen: f["Aantal draaidagen (COUNT)"] || 0,
        value,
      });
      entry.totalProjects += 1;
      if (ACTIVE_STATUSES.includes(f.Status)) entry.activeProjects += 1;
      entry.byStatus[f.Status] = (entry.byStatus[f.Status] || 0) + 1;
      entry.totalTaken += Number(f["Aantal taken"] || 0);
      entry.totalDeliverables += Number(f["Aantal deliverables"] || 0);
      entry.totalDraaidagen += Number(f["Aantal draaidagen (COUNT)"] || 0);
      entry.totalValue = (entry.totalValue || 0) + value;

      // Splits opdrachten (bevestigd) vs offertes/leads (potentieel)
      if (OPDRACHT_STATUSES.includes(f.Status)) {
        entry.opdrachtenCount = (entry.opdrachtenCount || 0) + 1;
        entry.opdrachtenValue = (entry.opdrachtenValue || 0) + value;
      } else if (OFFERTE_STATUSES.includes(f.Status)) {
        entry.offertesCount = (entry.offertesCount || 0) + 1;
        entry.offertesValue = (entry.offertesValue || 0) + value;
      }
      // Backwards-compatibel veld
      if (ACTIVE_STATUSES.includes(f.Status)) {
        entry.activeValue = (entry.activeValue || 0) + value;
      }
    }
  }

  // Sort: drukste PL eerst (opdrachten value > opdrachten count > offertes value)
  return [...byPL.values()].sort((a, b) => {
    const av = a.opdrachtenValue || 0, bv = b.opdrachtenValue || 0;
    if (av !== bv) return bv - av;
    const ac = a.opdrachtenCount || 0, bc = b.opdrachtenCount || 0;
    if (ac !== bc) return bc - ac;
    return (b.offertesValue || 0) - (a.offertesValue || 0);
  });
}

function PLWorkloadSection({ plWorkload }) {
  // Hele sectie collapse + meerdere PLs tegelijk uitklappen
  const [sectionOpen, setSectionOpen] = useState(true);
  const [expandedSet, setExpandedSet] = useState(() => new Set());

  const toggleOne = (name) => {
    setExpandedSet((prev) => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return next;
    });
  };
  const expandAll = () => setExpandedSet(new Set(plWorkload.map((p) => p.naam)));
  const collapseAll = () => setExpandedSet(new Set());
  const allOpen = expandedSet.size === plWorkload.length && plWorkload.length > 0;

  return (
    <Paper sx={{ p: { xs: 2, md: 3 }, mb: 3, borderRadius: 3, border: "1px solid #e5e7eb" }}>
      <Stack
        direction="row"
        alignItems="center"
        spacing={1}
        sx={{ mb: sectionOpen ? 2 : 0, cursor: "pointer", userSelect: "none" }}
        onClick={() => setSectionOpen(!sectionOpen)}
      >
        {sectionOpen ? <ExpandLessIcon sx={{ color: "#6366f1" }} /> : <ExpandMoreIcon sx={{ color: "#6366f1" }} />}
        <AssignmentIcon sx={{ color: "#6366f1" }} />
        <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
          Projectleiders — workload
        </Typography>
        <Chip label={`${plWorkload.length} PLs`} size="small" sx={{ ml: 1 }} />
        <Box sx={{ flex: 1 }} />
        {sectionOpen && (
          <Chip
            label={allOpen ? "Klap alles dicht" : "Klap alles open"}
            size="small"
            clickable
            onClick={(e) => { e.stopPropagation(); (allOpen ? collapseAll : expandAll)(); }}
            icon={allOpen ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
            sx={{ bgcolor: "#ede9fe", color: "#6d28d9", fontWeight: 600, "&:hover": { bgcolor: "#ddd6fe" } }}
          />
        )}
      </Stack>
      <Collapse in={sectionOpen}><Box sx={{ pt: 0 }}>

      {plWorkload.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          Geen projecten gevonden.
        </Typography>
      ) : (
        <Stack spacing={1}>
          {plWorkload.map((pl) => {
            const isOpen = expandedSet.has(pl.naam);
            return (
              <Box key={pl.naam}>
                <Box
                  onClick={() => toggleOne(pl.naam)}
                  sx={{
                    display: "grid",
                    gridTemplateColumns: { xs: "1fr auto", md: "1.4fr 1.3fr 0.7fr 0.7fr 0.7fr 0.9fr 0.9fr auto" },
                    gap: 1.5,
                    alignItems: "center",
                    px: 1.5, py: 1, borderRadius: 2,
                    cursor: "pointer", bgcolor: isOpen ? "#f5f3ff" : "transparent",
                    "&:hover": { bgcolor: "#fafafa" },
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                    <PersonIcon sx={{ color: "#94a3b8", fontSize: 18 }} />
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{pl.naam}</Typography>
                  </Box>
                  <Box sx={{ display: { xs: "none", md: "flex" }, gap: 0.5, flexWrap: "wrap" }}>
                    {Object.entries(pl.byStatus).map(([status, count]) => (
                      <Chip
                        key={status}
                        label={`${status}: ${count}`}
                        size="small"
                        sx={{ height: 20, fontSize: 11, bgcolor: statusColor(status), color: "white" }}
                      />
                    ))}
                  </Box>
                  <StatPill label="Opdrachten" value={pl.opdrachtenCount || 0} />
                  <StatPill label="Taken" value={pl.totalTaken} />
                  <StatPill label="Draaidagen" value={pl.totalDraaidagen} />
                  <Tooltip title={`${pl.opdrachtenCount || 0} opdrachten (In progress) — bevestigd werk`} arrow>
                    <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-start", cursor: "help" }}>
                      <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1, fontSize: 16, color: "#10b981" }}>
                        {fmtEuro(pl.opdrachtenValue || 0)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ fontSize: 10 }}>
                        Opdrachten ({pl.opdrachtenCount || 0})
                      </Typography>
                    </Box>
                  </Tooltip>
                  <Tooltip title={`${pl.offertesCount || 0} offertes/leads — nog niet bevestigd`} arrow>
                    <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-start", cursor: "help" }}>
                      <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1, fontSize: 16, color: "#3b82f6", opacity: 0.7 }}>
                        {fmtEuro(pl.offertesValue || 0)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ fontSize: 10 }}>
                        Offertes ({pl.offertesCount || 0})
                      </Typography>
                    </Box>
                  </Tooltip>
                  <Box>{isOpen ? <ExpandLessIcon /> : <ExpandMoreIcon />}</Box>
                </Box>
                <Collapse in={isOpen}>
                  <Box sx={{ pl: 4, pr: 2, py: 1.5 }}>
                    <ProjectGroup
                      title="Opdrachten"
                      sublabel="bevestigd werk — In progress"
                      accent="#10b981"
                      projects={pl.projects.filter((p) => OPDRACHT_STATUSES.includes(p.status))}
                    />
                    <ProjectGroup
                      title="Offertes & Leads"
                      sublabel="nog niet bevestigd — kunnen nog vervallen"
                      accent="#3b82f6"
                      faded
                      projects={pl.projects.filter((p) => OFFERTE_STATUSES.includes(p.status))}
                    />
                    {/* Andere statuses (Afgerond, On Hold, etc.) tonen we apart, ingeklapt */}
                    {pl.projects.some((p) => !OPDRACHT_STATUSES.includes(p.status) && !OFFERTE_STATUSES.includes(p.status)) && (
                      <ProjectGroup
                        title="Overig"
                        sublabel="afgerond / on hold / gearchiveerd"
                        accent="#9ca3af"
                        faded
                        defaultCollapsed
                        projects={pl.projects.filter((p) => !OPDRACHT_STATUSES.includes(p.status) && !OFFERTE_STATUSES.includes(p.status))}
                      />
                    )}
                  </Box>
                </Collapse>
              </Box>
            );
          })}
        </Stack>
      )}
      </Box></Collapse>
    </Paper>
  );
}

function ProjectGroup({ title, sublabel, accent, projects, faded = false, defaultCollapsed = false }) {
  const [open, setOpen] = useState(!defaultCollapsed);
  if (projects.length === 0) return null;

  const totalValue = projects.reduce((sum, p) => sum + (p.value || 0), 0);

  return (
    <Box sx={{ mb: 2, opacity: faded ? 0.85 : 1 }}>
      <Box
        onClick={() => setOpen(!open)}
        sx={{
          display: "flex", alignItems: "center", gap: 1,
          py: 0.75, mb: 0.5, cursor: "pointer",
          borderBottom: `2px solid ${accent}`,
        }}
      >
        <Box sx={{ width: 8, height: 8, borderRadius: "50%", bgcolor: accent }} />
        <Typography variant="body2" sx={{ fontWeight: 700, color: accent, textTransform: "uppercase", fontSize: 11, letterSpacing: 0.3 }}>
          {title}
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ fontSize: 11 }}>
          {projects.length} {projects.length === 1 ? "project" : "projecten"} • {fmtEuro(totalValue)} • {sublabel}
        </Typography>
        <Box sx={{ flex: 1 }} />
        {open ? <ExpandLessIcon sx={{ fontSize: 16, color: "text.secondary" }} /> : <ExpandMoreIcon sx={{ fontSize: 16, color: "text.secondary" }} />}
      </Box>
      <Collapse in={open}>
        <Stack spacing={0.5}>
          {projects
            .sort((a, b) => (b.value || 0) - (a.value || 0) || (b.taken + b.draaidagen * 5) - (a.taken + a.draaidagen * 5))
            .map((proj) => (
              <Box key={proj.nummer + proj.naam} sx={{
                display: "grid",
                gridTemplateColumns: { xs: "60px 1fr", md: "60px 2fr 1.5fr 80px 50px 50px 50px 70px" },
                gap: 1, alignItems: "center", fontSize: 12,
                py: 0.5, borderBottom: "1px solid #f1f5f9",
              }}>
                <Typography variant="caption" sx={{ fontFamily: "monospace", color: "#94a3b8" }}>
                  {proj.nummer}
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 500, fontSize: 13 }}>
                  {proj.naam}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: { xs: "none", md: "block" } }}>
                  {proj.klant}
                </Typography>
                <Chip
                  label={proj.status}
                  size="small"
                  sx={{ height: 18, fontSize: 10, bgcolor: statusColor(proj.status), color: "white", display: { xs: "none", md: "flex" } }}
                />
                <Tooltip title={`${proj.taken} taken`} arrow>
                  <Typography variant="caption" sx={{ display: { xs: "none", md: "block" }, cursor: "help" }}>T: {proj.taken}</Typography>
                </Tooltip>
                <Tooltip title={`${proj.deliverables} deliverables`} arrow>
                  <Typography variant="caption" sx={{ display: { xs: "none", md: "block" }, cursor: "help" }}>D: {proj.deliverables}</Typography>
                </Tooltip>
                <Tooltip title={`${proj.draaidagen} draaidagen`} arrow>
                  <Typography variant="caption" sx={{ display: { xs: "none", md: "block" }, cursor: "help" }}>Dd: {proj.draaidagen}</Typography>
                </Tooltip>
                <Typography variant="caption" sx={{ display: { xs: "none", md: "block" }, fontWeight: 600, color: "#0d9488" }}>
                  {fmtEuro(proj.value)}
                </Typography>
              </Box>
            ))}
        </Stack>
      </Collapse>
    </Box>
  );
}

function StatPill({ label, value, active }) {
  return (
    <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-start" }}>
      <Typography variant="h6" sx={{ fontWeight: 700, lineHeight: 1, fontSize: 16 }}>
        {value}
        {active !== undefined && active !== value && (
          <Typography component="span" variant="caption" sx={{ color: "#10b981", ml: 0.5, fontSize: 11 }}>
            ({active} actief)
          </Typography>
        )}
      </Typography>
      <Typography variant="caption" color="text.secondary" sx={{ fontSize: 10 }}>{label}</Typography>
    </Box>
  );
}

function statusColor(status) {
  const map = {
    "In progress": "#10b981",
    "Lead": "#f59e0b",
    "Offerte": "#3b82f6",
    "On Hold": "#8b5cf6",
    "Afgerond": "#6b7280",
    "Geannuleerd": "#ef4444",
    "Gearchiveerd": "#9ca3af",
  };
  return map[status] || "#94a3b8";
}

// ── Crew Load (cameramannen + regisseurs) ────────────────────────────────────

function buildCrewLoad(draaidagen, personen) {
  // Index personen op naam/email + bouw role-map vanuit Airtable function veld.
  // Stagiairs filteren we eruit (type === "Stagiair").
  // Bij meerdere functies (bv. "Regisseur, Cameraman" voor Michel): regie wint.
  const personByEmail = new Map();
  const personByName = new Map();
  const personByFirstName = new Map();
  const personByAirtableId = new Map();  // Voor legacy records waar crew als rec-ID's bewaard zijn

  for (const p of personen) {
    const naam = p.name || p["Volledige naam"] || "";
    const firstName = p.firstName || p["Voornaam"] || naam.split(/\s+/)[0] || "";
    const email = (p.email || p.Email || "").toLowerCase();
    const type = p.type || p.Type || "";
    const functionStr = (p.function || p.Functie || p.functie || "").toString();
    const airtableId = p.id || "";

    // Stagiairs eruit
    if (type === "Stagiair") continue;
    if (!naam) continue;

    // Bepaal primaire rol uit functie-string (kan meerdere comma-separated zijn)
    const fnLower = functionStr.toLowerCase();
    let role = null;
    if (FORCED_CAMERAMEN_EMAILS.has(email)) {
      // Generieke "Freelancer"-placeholder altijd als cameraman classificeren
      role = "cameraman";
    } else if (fnLower.includes("regisseur")) {
      role = "regisseur";
    } else if (fnLower.includes("cameraman") || fnLower.includes("camera operator")) {
      role = SENIOR_CAMERAMEN_EMAILS.has(email) ? "cameramanSenior" : "cameraman";
    }
    if (!role) continue;

    const meta = { naam, firstName, email, role };
    if (email) personByEmail.set(email, meta);
    personByName.set(naam.toLowerCase(), meta);
    if (firstName) {
      const fl = firstName.toLowerCase();
      if (!personByFirstName.has(fl)) personByFirstName.set(fl, meta);
    }
    if (airtableId) personByAirtableId.set(airtableId, meta);
  }

  // Resolve crew-naam uit een draaidag → canonical persoon, of null
  // Sommige PG-records bewaren crew als Airtable record-IDs i.p.v. namen
  // (legacy data); dan loopt naam-lookup mis maar werkt airtable_id lookup wel.
  function resolvePerson(rawNaam, rawEmail) {
    const naamLower = (rawNaam || "").toLowerCase().trim();
    const emailLower = (rawEmail || "").toLowerCase().trim();

    if (emailLower && personByEmail.has(emailLower)) return personByEmail.get(emailLower);

    // Detecteer of we een Airtable record-ID hebben i.p.v. een naam
    if (looksLikeAirtableId(rawNaam) && personByAirtableId.has(rawNaam)) {
      return personByAirtableId.get(rawNaam);
    }

    if (naamLower && personByName.has(naamLower)) return personByName.get(naamLower);
    const first = naamLower.split(/\s+/)[0];
    if (first && personByFirstName.has(first)) return personByFirstName.get(first);
    return null;
  }

  const ONE_DAY_MS = 86400000;
  const byPerson = new Map();
  for (const dd of draaidagen) {
    const startDate = dd.startDatum ? new Date(dd.startDatum) : null;
    if (!startDate) continue;
    startDate.setHours(0, 0, 0, 0);
    const endDate = new Date(dd.eindDatum || dd.startDatum);
    endDate.setHours(0, 0, 0, 0);
    const isOptie = !!dd.isOptie;

    // Verzamel alle kalenderdagen — multi-day draaidag telt N keer
    const dayDates = [];
    for (let cur = new Date(startDate); cur <= endDate; cur = new Date(cur.getTime() + ONE_DAY_MS)) {
      dayDates.push(new Date(cur));
    }
    if (dayDates.length === 0) dayDates.push(startDate);

    const crewNames = dd.crewNaam || [];
    const crewEmails = dd.crewEmail || [];

    crewNames.forEach((rawNaam, idx) => {
      if (!rawNaam) return;
      const resolved = resolvePerson(rawNaam, crewEmails[idx] || "");
      if (!resolved) return;

      const key = resolved.email || resolved.naam.toLowerCase().trim();
      if (!byPerson.has(key)) {
        byPerson.set(key, {
          naam: resolved.naam,
          email: resolved.email,
          role: resolved.role,
          target: TARGETS[resolved.role],
          byWeek: {},        // per-week: { confirmed: N, opties: N }
          draaidagen: [],
        });
      }
      const entry = byPerson.get(key);
      // Tel elke dag in de juiste week-bucket
      for (const dayDate of dayDates) {
        const wk = weekKey(dayDate);
        if (!entry.byWeek[wk]) entry.byWeek[wk] = { confirmed: 0, opties: 0 };
        if (isOptie) entry.byWeek[wk].opties += 1;
        else entry.byWeek[wk].confirmed += 1;
      }
      entry.draaidagen.push({
        id: dd.id,
        date: startDate,
        endDate,
        startDatum: dd.startDatum,
        eindDatum: dd.eindDatum,
        days: dd.days || 1,
        isOptie,
        wk: weekKey(startDate),
        projectnummer: dd.projectnummer,
        projectnaam: dd.projectnaam,
        draaidagNaam: dd.naam,  // Fallback voor wanneer projectnaam ontbreekt
        klantnaam: dd.klantnaam,
        locatie: dd.locatie,
        crewNaam: dd.crewNaam,  // Voor debug mode
      });
    });
  }

  const order = { regisseur: 0, cameramanSenior: 1, cameraman: 2 };
  return [...byPerson.values()].sort((a, b) => {
    if (order[a.role] !== order[b.role]) return order[a.role] - order[b.role];
    return a.naam.localeCompare(b.naam, "nl");
  });
}

function CrewLoadSection({ crewLoad, debugMode }) {
  const [sectionOpen, setSectionOpen] = useState(true);
  const [tab, setTab] = useState("regisseurs");
  const [expandedKey, setExpandedKey] = useState(null);
  const filtered = crewLoad.filter((p) => {
    if (tab === "regisseurs") return p.role === "regisseur";
    return p.role === "cameraman" || p.role === "cameramanSenior";
  });

  // Bepaal de weeks-as: huidige week + 3 volgende
  const today = new Date();
  const weeks = [];
  for (let i = 0; i < 4; i++) {
    const wkStart = startOfWeek(today);
    wkStart.setDate(wkStart.getDate() + i * 7);
    weeks.push({ key: weekKey(wkStart), date: new Date(wkStart) });
  }

  return (
    <Paper sx={{ p: { xs: 2, md: 3 }, mb: 3, borderRadius: 3, border: "1px solid #e5e7eb" }}>
      <Stack
        direction="row"
        alignItems="center"
        spacing={1}
        sx={{ mb: sectionOpen ? 2 : 0, cursor: "pointer", userSelect: "none" }}
        onClick={() => setSectionOpen(!sectionOpen)}
      >
        {sectionOpen ? <ExpandLessIcon sx={{ color: "#ec4899" }} /> : <ExpandMoreIcon sx={{ color: "#ec4899" }} />}
        <LocalMoviesIcon sx={{ color: "#ec4899" }} />
        <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
          Crew-bezetting — komende 4 weken
        </Typography>
      </Stack>

      <Collapse in={sectionOpen}><Box>

      <Tabs
        value={tab}
        onChange={(_, v) => setTab(v)}
        sx={{ mb: 2, borderBottom: "1px solid #e5e7eb" }}
      >
        <Tab value="regisseurs" label={<Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}><MovieFilterIcon fontSize="small" /> Regisseurs</Box>} sx={{ textTransform: "none" }} />
        <Tab value="camera" label={<Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}><VideocamIcon fontSize="small" /> Camera</Box>} sx={{ textTransform: "none" }} />
      </Tabs>

      {filtered.length === 0 ? (
        <Typography variant="body2" color="text.secondary">
          Geen {tab === "regisseurs" ? "regisseurs" : "cameramannen"} met aankomende draaidagen gevonden.
        </Typography>
      ) : (
        <Box sx={{ overflowX: "auto" }}>
          <Box sx={{
            display: "grid",
            gridTemplateColumns: `220px repeat(${weeks.length}, 1fr) 80px`,
            gap: 1, alignItems: "center", mb: 1, pb: 1,
            borderBottom: "1px solid #e5e7eb", minWidth: 700,
          }}>
            <Typography variant="caption" sx={{ fontWeight: 600, color: "text.secondary" }}>Persoon</Typography>
            {weeks.map((w) => (
              <Box key={w.key} sx={{ textAlign: "center", lineHeight: 1.1 }}>
                <Typography variant="caption" sx={{ fontWeight: 600, color: "text.secondary", display: "block" }}>
                  {fmtWeekLabel(w.date)}
                </Typography>
                <Typography variant="caption" sx={{ color: "text.secondary", fontSize: 10, opacity: 0.7 }}>
                  {fmtWeekDateRange(w.date)}
                </Typography>
              </Box>
            ))}
            <Typography variant="caption" sx={{ fontWeight: 600, color: "text.secondary", textAlign: "right" }}>Target</Typography>
          </Box>

          {filtered.map((person) => {
            const personKey = person.email || person.naam;
            const isOpen = expandedKey === personKey;
            return (
              <React.Fragment key={personKey}>
                <Box
                  onClick={() => setExpandedKey(isOpen ? null : personKey)}
                  sx={{
                    display: "grid",
                    gridTemplateColumns: `220px repeat(${weeks.length}, 1fr) 80px`,
                    gap: 1, alignItems: "center",
                    py: 1, borderBottom: "1px solid #f1f5f9", minWidth: 700,
                    cursor: "pointer",
                    bgcolor: isOpen ? "#fdf4ff" : "transparent",
                    "&:hover": { bgcolor: "#fafafa" },
                  }}
                >
                  <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                    {isOpen ? <ExpandLessIcon sx={{ fontSize: 16, color: "text.secondary" }} /> : <ExpandMoreIcon sx={{ fontSize: 16, color: "text.secondary" }} />}
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>{person.naam}</Typography>
                    {person.role === "cameramanSenior" && (
                      <Chip label="Senior" size="small" sx={{ height: 16, fontSize: 9, bgcolor: "#fce7f3", color: "#9f1239" }} />
                    )}
                  </Box>
                  {weeks.map((w) => {
                    const cell = person.byWeek[w.key] || { confirmed: 0, opties: 0 };
                    const confirmed = cell.confirmed || 0;
                    const opties = cell.opties || 0;
                    const totalForTarget = confirmed + opties;
                    const [lo, hi] = person.target.ideal;
                    // Kleur op basis van totaal (bevestigd + opties), zoals user wil
                    let bg = "#f9fafb";
                    let color = "#374151";
                    if (totalForTarget > hi) { bg = "#fee2e2"; color = "#b91c1c"; }
                    else if (totalForTarget < lo) { bg = "#dbeafe"; color = "#1e40af"; }
                    else if (totalForTarget > 0) { bg = "#dcfce7"; color = "#166534"; }
                    return (
                      <Tooltip
                        key={w.key}
                        title={opties > 0 ? `${confirmed} bevestigd + ${opties} optie${opties === 1 ? "" : "s"} = ${totalForTarget} totaal` : `${confirmed} draaidag${confirmed === 1 ? "" : "en"}`}
                        arrow
                      >
                        <Box
                          sx={{
                            bgcolor: bg, color,
                            borderRadius: 1.5, py: 0.75, textAlign: "center",
                            fontWeight: 600, fontSize: 14, cursor: "help",
                          }}
                        >
                          {confirmed}
                          {opties > 0 && (
                            <Typography component="span" sx={{ fontSize: 11, opacity: 0.7, ml: 0.5, fontWeight: 500 }}>
                              +{opties}
                            </Typography>
                          )}
                        </Box>
                      </Tooltip>
                    );
                  })}
                  <Typography variant="caption" color="text.secondary" sx={{ textAlign: "right" }}>
                    {person.target.ideal[0] === person.target.ideal[1]
                      ? person.target.ideal[0]
                      : `${person.target.ideal[0]}-${person.target.ideal[1]}`} /wk
                  </Typography>
                </Box>
                <Collapse in={isOpen}>
                  <Box sx={{ pl: 4, py: 1.5, bgcolor: "#fdf4ff", borderBottom: "1px solid #f1f5f9" }}>
                    <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 600, display: "block", mb: 0.5 }}>
                      Aankomende draaidagen ({person.draaidagen.length})
                    </Typography>
                    {person.draaidagen.length === 0 ? (
                      <Typography variant="caption" color="text.secondary">Geen draaidagen.</Typography>
                    ) : (
                      <Stack spacing={0.5}>
                        {[...person.draaidagen].sort((a, b) => a.date - b.date).map((dd) => {
                          const klant = looksLikeAirtableId(dd.klantnaam) ? "" : (dd.klantnaam || "");
                          const locatie = looksLikeAirtableId(dd.locatie) ? "" : (dd.locatie || "");
                          return (
                            <Box key={dd.id} sx={{
                              opacity: dd.isOptie ? 0.7 : 1,
                              borderBottom: "1px solid #f1f5f9",
                              py: 0.5,
                            }}>
                              <Box sx={{
                                display: "grid",
                                gridTemplateColumns: { xs: "100px 1fr", md: "110px 80px 70px 2fr 1.5fr" },
                                gap: 1, alignItems: "start", fontSize: 12,
                              }}>
                                <Box sx={{ display: "flex", flexDirection: "column", gap: 0.25 }}>
                                  <Typography variant="caption" sx={{ fontWeight: 600, color: dd.isOptie ? "#94a3b8" : "#6d28d9" }}>
                                    {dd.date.toLocaleDateString("nl-NL", { weekday: "short", day: "2-digit", month: "short" })}
                                  </Typography>
                                </Box>
                                <Box sx={{ display: { xs: "none", md: "flex" }, gap: 0.25, flexWrap: "wrap" }}>
                                  {dd.days > 1 && (
                                    <Chip label={`${dd.days} dgn`} size="small" sx={{ height: 16, fontSize: 9, bgcolor: "#ede9fe", color: "#6d28d9" }} />
                                  )}
                                  {dd.isOptie && (
                                    <Chip label="OPTIE" size="small" sx={{ height: 16, fontSize: 9, bgcolor: "#fef3c7", color: "#92400e" }} />
                                  )}
                                </Box>
                                <Typography variant="caption" sx={{ fontFamily: "monospace", color: "#94a3b8", display: { xs: "none", md: "block" } }}>
                                  {dd.projectnummer}
                                </Typography>
                                <Box>
                                  <Typography variant="caption" sx={{ fontWeight: 500, display: "block" }}>
                                    {dd.projectnaam || dd.draaidagNaam || "(geen naam)"}
                                  </Typography>
                                  {klant && (
                                    <Typography variant="caption" sx={{ color: "#6366f1", fontSize: 11, display: "block" }}>
                                      🏢 {klant}
                                    </Typography>
                                  )}
                                </Box>
                                <Typography variant="caption" color="text.secondary" sx={{ display: { xs: "none", md: "block" } }}>
                                  {locatie}
                                </Typography>
                              </Box>
                              {debugMode && (
                                <Box sx={{
                                  mt: 0.5, p: 0.75, bgcolor: "#fff", borderRadius: 0.5,
                                  border: "1px dashed #cbd5e1", fontFamily: "monospace", fontSize: 10,
                                  color: "#475569", overflowX: "auto",
                                }}>
                                  <div>id: {dd.id} • startDatum: {dd.startDatum} • eindDatum: {dd.eindDatum} • days: {dd.days}</div>
                                  <div>projectnummer: "{dd.projectnummer}" • klantnaam (raw): "{dd.klantnaam}"</div>
                                  <div>crewNaam: [{(dd.crewNaam || []).map((n) => `"${n}"`).join(", ")}]</div>
                                </Box>
                              )}
                            </Box>
                          );
                        })}
                      </Stack>
                    )}
                  </Box>
                </Collapse>
              </React.Fragment>
            );
          })}
        </Box>
      )}

      <Box sx={{ mt: 2, display: "flex", gap: 2, flexWrap: "wrap", alignItems: "center" }}>
        <LegendDot bg="#dcfce7" color="#166534" label="Op target" />
        <LegendDot bg="#dbeafe" color="#1e40af" label="Onder target (rustig)" />
        <LegendDot bg="#fee2e2" color="#b91c1c" label="Boven target (druk)" />
        <Typography variant="caption" color="text.secondary" sx={{ fontSize: 10 }}>
          • <strong>+N</strong> = OPTIE-draaidagen (nog niet zeker; tellen wél mee voor target)
        </Typography>
      </Box>
      </Box></Collapse>
    </Paper>
  );
}

function LegendDot({ bg, color, label }) {
  return (
    <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
      <Box sx={{ width: 14, height: 14, borderRadius: 0.75, bgcolor: bg, border: `1px solid ${color}` }} />
      <Typography variant="caption" color="text.secondary">{label}</Typography>
    </Box>
  );
}

// ── Editors / Animatoren load — uren-gebaseerd per week ─────────────────────

function UitvoerderLoadSection({ personen, refreshKey }) {
  const [sectionOpen, setSectionOpen] = useState(true);
  const [tab, setTab] = useState("editors");
  const [tasksByPerson, setTasksByPerson] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [expandedKey, setExpandedKey] = useState(null);

  // Filter personen op functie - editors/animators
  // NB: function-veld kan string ("Editor, Animator") óf array (["Editor","Animator"]) zijn,
  // afhankelijk of het door PG sync (string) of direct uit Airtable (multipleSelects array) komt.
  const uitvoerders = useMemo(() => {
    const fnStr = (p) => {
      const raw = p.function || p.Functie || p.functie || "";
      return Array.isArray(raw) ? raw.join(", ") : String(raw);
    };
    return (personen || [])
      .filter((p) => {
        const t = p.type || p.Type || "";
        if (t === "Stagiair") return false;
        const fn = fnStr(p).toLowerCase();
        return fn.includes("editor") || fn.includes("animator");
      })
      .map((p) => {
        const fn = fnStr(p).toLowerCase();
        return {
          id: p.id,
          naam: p.name || p["Volledige naam"] || "",
          firstName: p.firstName || p["Voornaam"] || "",
          email: (p.email || p.Email || "").toLowerCase(),
          type: p.type || p.Type || "",
          functie: fnStr(p),
          isEditor: fn.includes("editor"),
          isAnimator: fn.includes("animator"),
        };
      })
      .sort((a, b) => a.naam.localeCompare(b.naam, "nl"));
  }, [personen]);

  // Bouw weken-as: deze week + 3 vooruit (zelfde grid als crew-bezetting)
  const today = new Date();
  const weeks = useMemo(() => {
    const out = [];
    for (let i = 0; i < 4; i++) {
      const wkStart = startOfWeek(today);
      wkStart.setDate(wkStart.getDate() + i * 7);
      out.push({ key: weekKey(wkStart), date: new Date(wkStart) });
    }
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshKey]);

  // Fetch taken zodra we personen ID's hebben
  useEffect(() => {
    if (!uitvoerders.length) return;
    let cancelled = false;
    async function load() {
      setLoading(true);
      setError(null);
      try {
        const startDate = weeks[0].date.toISOString().slice(0, 10);
        const endDate = new Date(weeks[weeks.length - 1].date);
        endDate.setDate(endDate.getDate() + 6);
        const endDateStr = endDate.toISOString().slice(0, 10);

        const params = new URLSearchParams();
        params.append("startDate", startDate);
        params.append("endDate", endDateStr);
        uitvoerders.forEach((u) => params.append("personIds[]", u.id));

        const res = await authFetch(`${BACKEND_URL}/Api/person-tasks-range?${params}`);
        if (cancelled) return;
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const json = await res.json();
        setTasksByPerson(json || {});
      } catch (e) {
        if (!cancelled) setError(e.message || "Kon taken niet laden");
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [uitvoerders.length, refreshKey]);

  // Aggregeer taken per persoon per week.
  // Uren worden proportioneel over werkdagen (ma-vr) tussen start- en einddatum
  // verdeeld — zelfde aanpak als CapacityOverview. Een 80u taak over 10 werkdagen
  // wordt 8u/dag, dus 40u in week 1 + 40u in week 2 (i.p.v. 80u in startweek).
  // Naast productie-uren tellen we ook afspraken/draaidagen/vrij apart per week
  // zodat user een compleet beeld heeft van iemands week.
  const personLoad = useMemo(() => {
    if (!tasksByPerson) return [];
    const ONE_DAY = 86400000;
    const taskCategory = (t) => {
      if (t.status === "Draaidag" || t.type === "Draaidag") return "draaidag";
      if (t.status === "Afspraak" || t.type === "Afspraak") return "afspraak";
      if (t.status === "Vrij/intern" || t.type === "Vrij/intern") return "vrij";
      return null;
    };
    return uitvoerders.map((p) => {
      const taken = tasksByPerson[p.id] || [];
      const byWeek = {};
      const byWeekBreakdown = {};
      const tasksByWeek = {};
      for (const w of weeks) {
        byWeek[w.key] = 0;
        byWeekBreakdown[w.key] = { afspraak: 0, draaidag: 0, vrij: 0 };
        tasksByWeek[w.key] = [];
      }
      // Splits taken in production (telt mee voor uren) vs filtered (zichtbaar in debug)
      const productionTasks = [];
      const filteredTasks = [];
      for (const t of taken) {
        if (isProductionTask(t)) productionTasks.push(t);
        else filteredTasks.push(t);
      }

      // Aggregeer per categorie ALLE taken (ook gefilterde) per week — voor mini-breakdown
      // Hours worden proportioneel verdeeld zelfde manier als productie
      for (const t of taken) {
        const cat = taskCategory(t);
        if (!cat) continue;
        if (!t.startDate) continue;
        const start = new Date(t.startDate); start.setHours(0, 0, 0, 0);
        const end = new Date(t.endDate || t.startDate); end.setHours(0, 0, 0, 0);
        if (end < start) continue;
        const workdays = [];
        for (let cur = new Date(start); cur <= end; cur = new Date(cur.getTime() + ONE_DAY)) {
          const dow = cur.getDay();
          if (dow !== 0 && dow !== 6) workdays.push(new Date(cur));
        }
        const totalHours = Number(t.hours || 0);
        if (workdays.length === 0 || totalHours === 0) continue;
        const hoursPerDay = totalHours / workdays.length;
        for (const day of workdays) {
          const wk = weekKey(day);
          if (byWeekBreakdown[wk]) byWeekBreakdown[wk][cat] += hoursPerDay;
        }
      }

      for (const t of productionTasks) {
        if (!t.startDate) continue;
        const start = new Date(t.startDate);
        start.setHours(0, 0, 0, 0);
        const end = new Date(t.endDate || t.startDate);
        end.setHours(0, 0, 0, 0);
        if (end < start) continue;

        // Verzamel alle werkdagen (ma-vr) tussen start en end inclusief
        const workdays = [];
        for (let cur = new Date(start); cur <= end; cur = new Date(cur.getTime() + ONE_DAY)) {
          const dow = cur.getDay();
          if (dow !== 0 && dow !== 6) workdays.push(new Date(cur));
        }
        const totalHours = Number(t.hours || 0);
        if (workdays.length === 0 || totalHours === 0) continue;
        const hoursPerDay = totalHours / workdays.length;

        // Verdeel uren over alle werkdagen, alleen als die werkdag in onze grid valt
        for (const day of workdays) {
          const wk = weekKey(day);
          if (byWeek[wk] === undefined) continue;
          byWeek[wk] += hoursPerDay;
        }
        // Voor click-through tonen we de taak in de week waarin hij start
        const startWk = weekKey(start);
        if (tasksByWeek[startWk] !== undefined) {
          tasksByWeek[startWk].push(t);
        }
      }
      return { ...p, taken: productionTasks, allTaken: taken, filteredTasks, byWeek, byWeekBreakdown, tasksByWeek };
    });
  }, [tasksByPerson, uitvoerders, weeks]);

  const filtered = personLoad.filter((p) => {
    if (tab === "editors") return p.isEditor;
    if (tab === "animators") return p.isAnimator;
    return true;
  });

  return (
    <Paper sx={{ p: { xs: 2, md: 3 }, mb: 3, borderRadius: 3, border: "1px solid #e5e7eb" }}>
      <Stack
        direction="row"
        alignItems="center"
        spacing={1}
        sx={{ mb: sectionOpen ? 2 : 0, cursor: "pointer", userSelect: "none" }}
        onClick={() => setSectionOpen(!sectionOpen)}
      >
        {sectionOpen ? <ExpandLessIcon sx={{ color: "#0d9488" }} /> : <ExpandMoreIcon sx={{ color: "#0d9488" }} />}
        <AssignmentIcon sx={{ color: "#0d9488" }} />
        <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
          Uitvoerders — werkdruk in uren
        </Typography>
        <Chip
          label={`${uitvoerders.length} personen`}
          size="small"
          sx={{ ml: 1 }}
        />
        {loading && <CircularProgress size={14} sx={{ ml: 1 }} />}
      </Stack>

      <Collapse in={sectionOpen}><Box>
        <Tabs
          value={tab}
          onChange={(_, v) => setTab(v)}
          sx={{ mb: 2, borderBottom: "1px solid #e5e7eb" }}
        >
          <Tab value="editors" label={<Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}><AssignmentIcon fontSize="small" /> Editors</Box>} sx={{ textTransform: "none" }} />
          <Tab value="animators" label={<Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}><AssignmentIcon fontSize="small" /> Animatoren</Box>} sx={{ textTransform: "none" }} />
        </Tabs>

        {error && <Alert severity="warning" sx={{ mb: 2 }}>{error}</Alert>}

        {filtered.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            Geen {tab} gevonden in personen-tabel.
          </Typography>
        ) : (
          <Box sx={{ overflowX: "auto" }}>
            <Box sx={{
              display: "grid",
              gridTemplateColumns: `220px repeat(${weeks.length}, 1fr) 80px`,
              gap: 1, alignItems: "center", mb: 1, pb: 1,
              borderBottom: "1px solid #e5e7eb", minWidth: 700,
            }}>
              <Typography variant="caption" sx={{ fontWeight: 600, color: "text.secondary" }}>Persoon</Typography>
              {weeks.map((w) => (
                <Box key={w.key} sx={{ textAlign: "center", lineHeight: 1.1 }}>
                  <Typography variant="caption" sx={{ fontWeight: 600, color: "text.secondary", display: "block" }}>
                    {fmtWeekLabel(w.date)}
                  </Typography>
                  <Typography variant="caption" sx={{ color: "text.secondary", fontSize: 10, opacity: 0.7 }}>
                    {fmtWeekDateRange(w.date)}
                  </Typography>
                </Box>
              ))}
              <Typography variant="caption" sx={{ fontWeight: 600, color: "text.secondary", textAlign: "right" }}>Totaal</Typography>
            </Box>

            {filtered.map((person) => {
              const personKey = person.email || person.id;
              const isOpen = expandedKey === personKey;
              const total = weeks.reduce((acc, w) => acc + (person.byWeek[w.key] || 0), 0);
              return (
                <React.Fragment key={personKey}>
                  <Box
                    onClick={() => setExpandedKey(isOpen ? null : personKey)}
                    sx={{
                      display: "grid",
                      gridTemplateColumns: `220px repeat(${weeks.length}, 1fr) 80px`,
                      gap: 1, alignItems: "center",
                      py: 1, borderBottom: "1px solid #f1f5f9", minWidth: 700,
                      cursor: "pointer",
                      bgcolor: isOpen ? "#ecfeff" : "transparent",
                      "&:hover": { bgcolor: "#fafafa" },
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                      {isOpen ? <ExpandLessIcon sx={{ fontSize: 16, color: "text.secondary" }} /> : <ExpandMoreIcon sx={{ fontSize: 16, color: "text.secondary" }} />}
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>{person.naam}</Typography>
                      {person.type === "Freelancer" && (
                        <Chip label="Freelance" size="small" sx={{ height: 16, fontSize: 9, bgcolor: "#f1f5f9", color: "#475569" }} />
                      )}
                    </Box>
                    {weeks.map((w) => {
                      const hours = person.byWeek[w.key] || 0;
                      const breakdown = person.byWeekBreakdown?.[w.key] || { afspraak: 0, draaidag: 0, vrij: 0 };
                      const afspr = Math.round(breakdown.afspraak);
                      const dd = Math.round(breakdown.draaidag);
                      const vrij = Math.round(breakdown.vrij);
                      const hasBreakdown = afspr > 0 || dd > 0 || vrij > 0;
                      // 0-30: groen, 30-40: oranje (richtlijn vol), 40+: rood (overcap)
                      let bg = "#f9fafb"; let color = "#374151";
                      if (hours >= 40) { bg = "#fee2e2"; color = "#b91c1c"; }
                      else if (hours >= 30) { bg = "#fef3c7"; color = "#92400e"; }
                      else if (hours > 0) { bg = "#dcfce7"; color = "#166534"; }

                      const tooltipText = `Productie: ${Math.round(hours)}u${
                        afspr > 0 ? ` • Afspraken: ${afspr}u` : ""
                      }${dd > 0 ? ` • Draaidagen: ${dd}u` : ""}${
                        vrij > 0 ? ` • Vrij/intern: ${vrij}u` : ""
                      }`;

                      return (
                        <Tooltip key={w.key} title={tooltipText} arrow>
                          <Box sx={{
                            bgcolor: bg, color,
                            borderRadius: 1.5, py: 0.5, textAlign: "center",
                            fontWeight: 600, fontSize: 13,
                            cursor: "help",
                          }}>
                            <div>{hours > 0 ? `${Math.round(hours)}u` : "0"}</div>
                            {hasBreakdown && (
                              <Box sx={{ fontSize: 9, fontWeight: 500, opacity: 0.7, mt: 0.25, lineHeight: 1.1 }}>
                                {afspr > 0 && <span>💬{afspr} </span>}
                                {dd > 0 && <span>🎬{dd} </span>}
                                {vrij > 0 && <span>🏖{vrij}</span>}
                              </Box>
                            )}
                          </Box>
                        </Tooltip>
                      );
                    })}
                    <Typography variant="caption" sx={{ textAlign: "right", fontWeight: 600, color: "#0d9488" }}>
                      {Math.round(total)}u
                    </Typography>
                  </Box>
                  <Collapse in={isOpen}>
                    <Box sx={{ pl: 4, py: 1.5, bgcolor: "#ecfeff", borderBottom: "1px solid #f1f5f9" }}>
                      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.75 }}>
                        <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 600 }}>
                          Productie-taken komende 4 weken ({person.taken?.length || 0})
                        </Typography>
                        {(person.filteredTasks?.length || 0) > 0 && (
                          <Tooltip title={`${person.filteredTasks.length} taken niet meegerekend (afspraken, draaidagen, vanallesuurtje, opgestuurd/afgerond, vrij). Zet debug aan om ze te zien.`}>
                            <Chip
                              label={`+${person.filteredTasks.length} niet-productie`}
                              size="small"
                              sx={{ height: 18, fontSize: 10, bgcolor: "#f1f5f9", color: "#64748b", cursor: "help" }}
                            />
                          </Tooltip>
                        )}
                      </Stack>
                      {(!person.taken || person.taken.length === 0) ? (
                        <Typography variant="caption" color="text.secondary">Geen productie-taken.</Typography>
                      ) : (
                        (() => {
                          // Groepeer taken per week-key voor visuele scheiding
                          const sorted = [...person.taken].sort((a, b) => (a.startDate || "").localeCompare(b.startDate || ""));
                          const byWk = new Map();
                          for (const t of sorted) {
                            if (!t.startDate) continue;
                            const wk = weekKey(new Date(t.startDate));
                            if (!byWk.has(wk)) byWk.set(wk, []);
                            byWk.get(wk).push(t);
                          }
                          return [...byWk.entries()].map(([wk, items]) => {
                            const wkStart = new Date(wk);
                            const totalHours = items.reduce((acc, t) => acc + Number(t.hours || 0), 0);
                            return (
                              <Box key={wk} sx={{ mb: 1.5 }}>
                                <Box sx={{
                                  py: 0.5, mb: 0.25, borderBottom: "1px solid #cbd5e1",
                                  display: "flex", alignItems: "baseline", gap: 1,
                                }}>
                                  <Typography variant="caption" sx={{ fontWeight: 700, color: "#0d9488", fontSize: 11 }}>
                                    {fmtWeekLabel(wkStart)} ({fmtWeekDateRange(wkStart)})
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: 10 }}>
                                    — {items.length} taak{items.length === 1 ? "" : "en"} • {Math.round(totalHours)}u
                                  </Typography>
                                </Box>
                                <Stack spacing={0.25}>
                                  {items.map((t) => (
                                    <Box key={t.id} sx={{
                                      display: "grid",
                                      gridTemplateColumns: { xs: "70px 1fr", md: "90px 80px 1fr 60px" },
                                      gap: 1, alignItems: "center", py: 0.15, fontSize: 12,
                                    }}>
                                      <Typography variant="caption" sx={{ fontWeight: 600, color: "#0d9488" }}>
                                        {t.startDate ? new Date(t.startDate).toLocaleDateString("nl-NL", { weekday: "short", day: "2-digit", month: "short" }) : "—"}
                                      </Typography>
                                      <Chip
                                        label={t.status || "—"}
                                        size="small"
                                        sx={{ height: 16, fontSize: 9, bgcolor: "#f1f5f9", color: "#475569", display: { xs: "none", md: "inline-flex" } }}
                                      />
                                      <Typography variant="caption" sx={{ fontWeight: 500 }}>{t.name || "(geen naam)"}</Typography>
                                      <Typography variant="caption" sx={{ textAlign: "right", color: "text.secondary" }}>
                                        {t.hours ? `${t.hours}u` : ""}
                                      </Typography>
                                    </Box>
                                  ))}
                                </Stack>
                              </Box>
                            );
                          });
                        })()
                      )}

                      {/* Categorie-secties voor niet-productie items: Afspraken / Draaidag-blokken / Vrij */}
                      <NonProductionSection
                        title="💬 Afspraken"
                        accent="#6366f1"
                        bg="#eef2ff"
                        tasks={(person.filteredTasks || []).filter((t) => t.status === "Afspraak" || t.type === "Afspraak")}
                      />
                      <NonProductionSection
                        title="🎬 Draaidag-blokken"
                        accent="#ec4899"
                        bg="#fdf2f8"
                        tasks={(person.filteredTasks || []).filter((t) => t.status === "Draaidag" || t.type === "Draaidag")}
                        helper="Draaidagen tellen niet voor productie-uren — al in Crew-bezetting verwerkt."
                      />
                      <NonProductionSection
                        title="🏖 Vrij / intern"
                        accent="#92400e"
                        bg="#fef3c7"
                        tasks={(person.filteredTasks || []).filter((t) => t.status === "Vrij/intern" || t.type === "Vrij/intern")}
                      />
                    </Box>
                  </Collapse>
                </React.Fragment>
              );
            })}
          </Box>
        )}

        <Box sx={{ mt: 2, display: "flex", gap: 2, flexWrap: "wrap", alignItems: "center" }}>
          <LegendDot bg="#dcfce7" color="#166534" label="< 30u (rustig)" />
          <LegendDot bg="#fef3c7" color="#92400e" label="30-40u (vol)" />
          <LegendDot bg="#fee2e2" color="#b91c1c" label="≥ 40u (overcap)" />
          <Typography variant="caption" color="text.secondary" sx={{ fontSize: 10 }}>
            • <strong>Hoofdgetal</strong> = productie-uren • <strong>💬 afspraken · 🎬 draaidagen · 🏖 vrij</strong> apart geteld
          </Typography>
        </Box>
      </Box></Collapse>
    </Paper>
  );
}

// ── NonProductionSection — collapsible lijst van afspraken/draaidagen/vrij per week ──

function NonProductionSection({ title, accent, bg, tasks, helper }) {
  const [open, setOpen] = useState(false);
  if (!tasks || tasks.length === 0) return null;

  // Bereken totaal-uren
  const totalHours = tasks.reduce((acc, t) => acc + Number(t.hours || 0), 0);

  // Groepeer per week
  const sorted = [...tasks].sort((a, b) => (a.startDate || "").localeCompare(b.startDate || ""));
  const byWk = new Map();
  for (const t of sorted) {
    if (!t.startDate) continue;
    const wk = weekKey(new Date(t.startDate));
    if (!byWk.has(wk)) byWk.set(wk, []);
    byWk.get(wk).push(t);
  }

  return (
    <Box sx={{ mt: 1.5, borderRadius: 1.5, overflow: "hidden", border: `1px solid ${bg}` }}>
      <Box
        onClick={() => setOpen(!open)}
        sx={{
          display: "flex", alignItems: "center", gap: 1, px: 1.25, py: 0.75,
          bgcolor: bg, cursor: "pointer", userSelect: "none",
          "&:hover": { filter: "brightness(0.97)" },
        }}
      >
        {open ? <ExpandLessIcon sx={{ fontSize: 16, color: accent }} /> : <ExpandMoreIcon sx={{ fontSize: 16, color: accent }} />}
        <Typography variant="caption" sx={{ fontWeight: 700, color: accent, fontSize: 11 }}>
          {title}
        </Typography>
        <Typography variant="caption" color="text.secondary" sx={{ fontSize: 10 }}>
          — {tasks.length} item{tasks.length === 1 ? "" : "s"} • {Math.round(totalHours)}u totaal
        </Typography>
      </Box>
      <Collapse in={open}>
        <Box sx={{ px: 1.25, py: 1, bgcolor: "#fff" }}>
          {helper && (
            <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 0.5, fontSize: 10, fontStyle: "italic" }}>
              {helper}
            </Typography>
          )}
          {[...byWk.entries()].map(([wk, items]) => {
            const wkStart = new Date(wk);
            const subTotal = items.reduce((acc, t) => acc + Number(t.hours || 0), 0);
            return (
              <Box key={wk} sx={{ mb: 1 }}>
                <Box sx={{ display: "flex", alignItems: "baseline", gap: 1, py: 0.25, borderBottom: "1px solid #f1f5f9" }}>
                  <Typography variant="caption" sx={{ fontWeight: 700, color: accent, fontSize: 10 }}>
                    {fmtWeekLabel(wkStart)} ({fmtWeekDateRange(wkStart)})
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: 10 }}>
                    — {items.length} • {Math.round(subTotal)}u
                  </Typography>
                </Box>
                <Stack spacing={0.2} sx={{ mt: 0.25 }}>
                  {items.map((t) => (
                    <Box key={t.id} sx={{
                      display: "grid",
                      gridTemplateColumns: { xs: "70px 1fr 50px", md: "90px 80px 1fr 60px" },
                      gap: 1, alignItems: "center", fontSize: 11, py: 0.15,
                    }}>
                      <Typography variant="caption" sx={{ fontWeight: 600, color: accent, fontSize: 10 }}>
                        {t.startDate ? new Date(t.startDate).toLocaleDateString("nl-NL", { weekday: "short", day: "2-digit", month: "short" }) : "—"}
                      </Typography>
                      <Chip
                        label={t.status || t.type || "—"}
                        size="small"
                        sx={{ height: 14, fontSize: 8, bgcolor: "#f1f5f9", color: "#475569", display: { xs: "none", md: "inline-flex" } }}
                      />
                      <Typography variant="caption" sx={{ fontWeight: 500, fontSize: 11 }}>
                        {t.name || "(geen naam)"}
                      </Typography>
                      <Typography variant="caption" sx={{ textAlign: "right", color: "text.secondary", fontSize: 10 }}>
                        {t.hours ? `${t.hours}u` : ""}
                      </Typography>
                    </Box>
                  ))}
                </Stack>
              </Box>
            );
          })}
        </Box>
      </Collapse>
    </Box>
  );
}

// ── KPI Totals Section — top of dashboard, "is het druk?" indicator ─────────

function KpiTotalsSection({ refreshKey, draaidagen }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [openDialog, setOpenDialog] = useState(null); // "draaidagen" | "tasks" | "meetings" | null

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      setError(null);
      try {
        const res = await authFetch(
          `${AI_ENGINE_URL}/api/v1/dashboard/totals?weeks_back=12&weeks_forward=4`
        );
        if (cancelled) return;
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const json = await res.json();
        setData(json);
      } catch (e) {
        if (!cancelled) setError(e.message || "Kon totalen niet laden");
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, [refreshKey]);

  if (loading) {
    return (
      <Paper sx={{ p: 2, mb: 3, borderRadius: 3, border: "1px solid #e5e7eb", display: "flex", justifyContent: "center" }}>
        <CircularProgress size={20} />
      </Paper>
    );
  }
  if (error) {
    return (
      <Alert severity="warning" sx={{ mb: 3 }}>KPI-totalen niet beschikbaar: {error}</Alert>
    );
  }
  if (!data) return null;

  return (
    <Box sx={{ mb: 3 }}>
      <Box sx={{
        display: "grid",
        gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr", md: "1fr 1fr 1fr" },
        gap: 2,
      }}>
        <KpiCard
          icon={<EventIcon />}
          label="Draaidagen deze week"
          accent="#ec4899"
          stats={data.draaidagen}
          sparklineKeys={data.weekKeys}
          currentWeekKey={data.currentWeekKey}
          unit="dd"
          onClick={() => setOpenDialog("draaidagen")}
        />
        <KpiCard
          icon={<AssignmentIcon />}
          label="Taken deze week"
          accent="#6366f1"
          stats={data.tasks}
          sparklineKeys={data.weekKeys}
          currentWeekKey={data.currentWeekKey}
          unit=""
          onClick={() => setOpenDialog("tasks")}
        />
        <KpiCard
          icon={<GroupsIcon />}
          label="Meetings deze week"
          accent="#f59e0b"
          stats={data.meetings}
          sparklineKeys={data.weekKeys}
          currentWeekKey={data.currentWeekKey}
          unit=""
          onClick={() => setOpenDialog("meetings")}
        />
      </Box>

      <KpiDetailDialog
        open={openDialog !== null}
        type={openDialog}
        onClose={() => setOpenDialog(null)}
        draaidagen={draaidagen}
        weekKeys={data.weekKeys}
        currentWeekKey={data.currentWeekKey}
      />
    </Box>
  );
}

// ── Detail dialog die opent bij klik op KPI card ────────────────────────────

function KpiDetailDialog({ open, type, onClose, draaidagen, weekKeys, currentWeekKey }) {
  if (!type) return null;

  const config = {
    draaidagen: { title: "Draaidagen — komende 4 weken", accent: "#ec4899", icon: <EventIcon /> },
    tasks: { title: "Taken — komende 4 weken", accent: "#6366f1", icon: <AssignmentIcon /> },
    meetings: { title: "Meetings — komende 4 weken", accent: "#f59e0b", icon: <GroupsIcon /> },
  }[type] || { title: "Details", accent: "#6366f1", icon: null };

  return (
    <MuiDialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <Box sx={{ p: 3 }}>
        <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 2 }}>
          <Box sx={{
            width: 36, height: 36, borderRadius: 2,
            bgcolor: config.accent, color: "white",
            display: "flex", alignItems: "center", justifyContent: "center",
          }}>
            {config.icon}
          </Box>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>{config.title}</Typography>
          <Box sx={{ flex: 1 }} />
          <IconButton onClick={onClose}><CloseIcon /></IconButton>
        </Stack>

        {type === "draaidagen" && <DraaidagenDetail draaidagen={draaidagen} />}

        {(type === "tasks" || type === "meetings") && (
          <Box>
            <Alert severity="info" sx={{ mb: 2 }}>
              Detailweergave voor {type === "tasks" ? "taken" : "meetings"} is in voorbereiding.
              Voor nu: gebruik de bestaande tools om individuele items te bekijken.
            </Alert>
            <Stack direction="row" spacing={1.5}>
              <Button
                variant="contained"
                href="/task-board"
                target="_blank"
                sx={{ bgcolor: config.accent, "&:hover": { bgcolor: config.accent, opacity: 0.9 } }}
              >
                Open Weekplanning →
              </Button>
              <Button
                variant="outlined"
                href="/planner-todo"
                target="_blank"
              >
                Open Planner To-Do
              </Button>
            </Stack>
          </Box>
        )}
      </Box>
    </MuiDialog>
  );
}

function DraaidagenDetail({ draaidagen }) {
  if (!draaidagen || draaidagen.length === 0) {
    return <Typography color="text.secondary">Geen draaidagen geladen.</Typography>;
  }
  // Sorteer op datum + groepeer per week
  const today = new Date();
  const monday = startOfWeek(today);
  const filtered = [...draaidagen]
    .filter((dd) => {
      const d = new Date(dd.startDatum);
      return d >= monday;
    })
    .sort((a, b) => new Date(a.startDatum) - new Date(b.startDatum));

  if (filtered.length === 0) {
    return <Typography color="text.secondary">Geen aankomende draaidagen.</Typography>;
  }

  // Groeperen per week-key
  const byWeek = new Map();
  for (const dd of filtered) {
    const wk = weekKey(new Date(dd.startDatum));
    if (!byWeek.has(wk)) byWeek.set(wk, []);
    byWeek.get(wk).push(dd);
  }

  return (
    <Box sx={{ maxHeight: 500, overflowY: "auto" }}>
      <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
        Totaal {filtered.length} draaidagen, gegroepeerd per week
      </Typography>
      {[...byWeek.entries()].map(([wk, items]) => {
        const wkStart = new Date(wk);
        const label = fmtWeekLabel(wkStart);
        const range = fmtWeekDateRange(wkStart);
        const confirmedCount = items.filter((dd) => !dd.isOptie).length;
        const optieCount = items.filter((dd) => dd.isOptie).length;
        return (
          <Box key={wk} sx={{ mb: 2 }}>
            <Stack direction="row" spacing={1} alignItems="baseline" sx={{ mb: 0.5 }}>
              <Typography variant="body2" sx={{ fontWeight: 700, color: "#ec4899" }}>
                {label} ({range})
              </Typography>
              <Typography variant="caption" color="text.secondary">
                — {items.length} totaal
                {optieCount > 0 && (
                  <>
                    {": "}
                    <Typography component="span" variant="caption" sx={{ color: "#0d9488", fontWeight: 600 }}>
                      {confirmedCount} bevestigd
                    </Typography>
                    {" + "}
                    <Typography component="span" variant="caption" sx={{ color: "#92400e", fontWeight: 600 }}>
                      {optieCount} optie{optieCount === 1 ? "" : "s"}
                    </Typography>
                  </>
                )}
              </Typography>
            </Stack>
            <Stack spacing={0.5}>
              {items.map((dd) => (
                <Box key={dd.id} sx={{
                  display: "grid",
                  gridTemplateColumns: { xs: "80px 1fr", md: "100px 70px 1fr 1fr 1fr" },
                  gap: 1, alignItems: "center", fontSize: 12, py: 0.4,
                  borderBottom: "1px solid #f1f5f9",
                  opacity: dd.isOptie ? 0.7 : 1,
                }}>
                  <Typography variant="caption" sx={{ fontWeight: 600, color: "#6d28d9" }}>
                    {new Date(dd.startDatum).toLocaleDateString("nl-NL", { weekday: "short", day: "2-digit", month: "short" })}
                  </Typography>
                  <Box sx={{ display: { xs: "none", md: "flex" }, gap: 0.25 }}>
                    {dd.days > 1 && <Chip label={`${dd.days} dgn`} size="small" sx={{ height: 16, fontSize: 9, bgcolor: "#ede9fe", color: "#6d28d9" }} />}
                    {dd.isOptie && <Chip label="OPTIE" size="small" sx={{ height: 16, fontSize: 9, bgcolor: "#fef3c7", color: "#92400e" }} />}
                  </Box>
                  <Typography variant="caption" sx={{ fontWeight: 500 }}>
                    {dd.projectnaam || dd.naam || "(geen naam)"}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: { xs: "none", md: "block" } }}>
                    {looksLikeAirtableId(dd.klantnaam) ? "" : (dd.klantnaam || "")}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ display: { xs: "none", md: "block" } }}>
                    {(dd.crewNaam || []).filter(n => !looksLikeAirtableId(n)).join(", ")}
                  </Typography>
                </Box>
              ))}
            </Stack>
          </Box>
        );
      })}
    </Box>
  );
}

function KpiCard({ icon, label, accent, stats, sparklineKeys, currentWeekKey, unit, onClick }) {
  // Trend-chip o.b.v. delta
  const delta = stats?.deltaPct;
  let TrendIcon = TrendingFlatIcon;
  let trendBg = "#f1f5f9";
  let trendColor = "#475569";
  let trendLabel = "—";
  if (delta != null) {
    if (delta > 15) {
      TrendIcon = TrendingUpIcon;
      trendBg = "#fee2e2";
      trendColor = "#b91c1c";
      trendLabel = `+${Math.round(delta)}% drukker`;
    } else if (delta < -15) {
      TrendIcon = TrendingDownIcon;
      trendBg = "#dbeafe";
      trendColor = "#1e40af";
      trendLabel = `${Math.round(delta)}% rustiger`;
    } else {
      TrendIcon = TrendingFlatIcon;
      trendBg = "#dcfce7";
      trendColor = "#166534";
      trendLabel = `${delta > 0 ? "+" : ""}${Math.round(delta)}% normaal`;
    }
  }

  return (
    <Paper
      onClick={onClick}
      sx={{
        p: 2, borderRadius: 3, border: "1px solid #e5e7eb",
        display: "flex", flexDirection: "column", gap: 1,
        cursor: onClick ? "pointer" : "default",
        transition: "all 0.15s",
        "&:hover": onClick ? {
          borderColor: accent, transform: "translateY(-1px)",
          boxShadow: "0 4px 12px rgba(0,0,0,0.06)",
        } : {},
      }}
    >
      <Stack direction="row" alignItems="center" spacing={1}>
        <Box sx={{
          width: 32, height: 32, borderRadius: 1.5,
          bgcolor: accent, color: "white",
          display: "flex", alignItems: "center", justifyContent: "center",
          "& svg": { fontSize: 18 },
        }}>
          {icon}
        </Box>
        <Typography variant="caption" sx={{ fontWeight: 600, color: "text.secondary", flex: 1 }}>
          {label}
        </Typography>
        {onClick && (
          <Typography variant="caption" sx={{ fontSize: 10, color: "text.secondary", opacity: 0.6 }}>
            klik voor detail →
          </Typography>
        )}
      </Stack>

      <Stack direction="row" alignItems="baseline" spacing={1.5} sx={{ mt: 0.5 }}>
        <Typography variant="h4" sx={{ fontWeight: 700, lineHeight: 1, color: accent }}>
          {stats?.current ?? 0}{unit && <Typography component="span" variant="body2" sx={{ ml: 0.5, color: "text.secondary" }}>{unit}</Typography>}
        </Typography>
        <Chip
          icon={<TrendIcon sx={{ fontSize: 14 }} />}
          label={trendLabel}
          size="small"
          sx={{
            height: 20, fontSize: 10, fontWeight: 600,
            bgcolor: trendBg, color: trendColor,
            "& .MuiChip-icon": { color: trendColor, ml: 0.5 },
          }}
        />
      </Stack>

      <Typography variant="caption" color="text.secondary" sx={{ fontSize: 11 }}>
        Gemiddelde laatste 4 wkn: <strong>{stats?.trailingAvg ?? 0}</strong>
        {stats?.futureTotal !== undefined && stats.futureTotal > 0 && (
          <> • Komende 4 wkn: <strong>{stats.futureTotal}</strong></>
        )}
      </Typography>

      <Sparkline
        weekKeys={sparklineKeys}
        byWeek={stats?.byWeek || {}}
        currentWeekKey={currentWeekKey}
        accent={accent}
      />
    </Paper>
  );
}

function Sparkline({ weekKeys, byWeek, currentWeekKey, accent }) {
  const values = weekKeys.map((wk) => byWeek[wk] || 0);
  const max = Math.max(...values, 1);
  const W = 220;
  const H = 36;
  const barWidth = W / values.length;

  return (
    <Box sx={{ mt: 0.5 }}>
      <svg width="100%" height={H} viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" style={{ display: "block" }}>
        {values.map((v, i) => {
          const isCurrent = weekKeys[i] === currentWeekKey;
          const isFuture = weekKeys[i] > currentWeekKey;
          const h = (v / max) * (H - 4);
          const x = i * barWidth + 1;
          const y = H - h - 1;
          return (
            <rect
              key={weekKeys[i]}
              x={x}
              y={y}
              width={Math.max(1, barWidth - 2)}
              height={Math.max(1, h)}
              fill={isCurrent ? accent : isFuture ? "#cbd5e1" : `${accent}55`}
              rx={1}
            />
          );
        })}
      </svg>
      <Box sx={{ display: "flex", justifyContent: "space-between", mt: 0.25 }}>
        <Typography variant="caption" sx={{ fontSize: 9, color: "text.secondary" }}>12 wkn terug</Typography>
        <Typography variant="caption" sx={{ fontSize: 9, color: "text.secondary" }}>nu</Typography>
        <Typography variant="caption" sx={{ fontSize: 9, color: "text.secondary" }}>+4 wkn</Typography>
      </Box>
    </Box>
  );
}

// ── History Section (terugkijken in de tijd) ─────────────────────────────────

function HistorySection() {
  const [range, setRange] = useState("12m"); // "3m" | "6m" | "12m" | "24m"
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      setError(null);
      try {
        const today = new Date();
        const months = { "3m": 3, "6m": 6, "12m": 12, "24m": 24 }[range] || 12;
        const FUTURE_MONTHS = 2;  // Sla ook 2 maanden vooruit op zodat we toekomstige boekingen zien
        const fromDate = new Date(today);
        fromDate.setMonth(fromDate.getMonth() - months);
        const toDate = new Date(today);
        toDate.setMonth(toDate.getMonth() + FUTURE_MONTHS);
        const fromIso = fromDate.toISOString().slice(0, 10);
        const toIso = toDate.toISOString().slice(0, 10);

        const res = await authFetch(
          `${AI_ENGINE_URL}/api/v1/dashboard/crew-history?from=${fromIso}&to=${toIso}`
        );
        if (cancelled) return;
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const json = await res.json();
        setData(json);
      } catch (e) {
        if (!cancelled) setError(e.message || "Kon geschiedenis niet laden");
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, [range]);

  // Bucket aggregeren naar maand als > 26 weken (anders te smal in de UI)
  const buckets = useMemo(() => {
    if (!data?.weekKeys) return null;
    const useMonth = data.weekKeys.length > 26;
    if (!useMonth) {
      return {
        granularity: "week",
        keys: data.weekKeys,
        labelFor: (k) => {
          const d = new Date(k);
          return `${d.getDate()}/${d.getMonth() + 1}`;
        },
        groupKey: (wk) => wk,
      };
    }
    // Group by YYYY-MM
    const monthKeys = new Set();
    for (const wk of data.weekKeys) monthKeys.add(wk.slice(0, 7));
    return {
      granularity: "month",
      keys: [...monthKeys].sort(),
      labelFor: (k) => {
        const [y, m] = k.split("-");
        const date = new Date(parseInt(y, 10), parseInt(m, 10) - 1, 1);
        // "mei" + apostrofe + 2-digit jaar — bv. "mei '25"
        const monthShort = date.toLocaleDateString("nl-NL", { month: "short" }).replace(".", "");
        return `${monthShort} '${y.slice(2)}`;
      },
      groupKey: (wk) => wk.slice(0, 7),
    };
  }, [data]);

  // Aggregate person.byWeek into bucketed values
  const personRows = useMemo(() => {
    if (!data?.persons || !buckets) return [];
    return data.persons.map((p) => {
      const byBucket = {};
      for (const [wk, count] of Object.entries(p.byWeek || {})) {
        const k = buckets.groupKey(wk);
        byBucket[k] = (byBucket[k] || 0) + count;
      }
      return { ...p, byBucket };
    });
  }, [data, buckets]);

  // Heatmap kleur-schaal
  const maxValue = useMemo(() => {
    let m = 0;
    for (const p of personRows) {
      for (const v of Object.values(p.byBucket || {})) {
        if (v > m) m = v;
      }
    }
    return m;
  }, [personRows]);

  // Team-breakdown per bucket — unieke draaidagen gecategoriseerd
  // Komt uit data.teamBreakdownByWeek (per week), aggregeer naar maand-buckets
  const teamBreakdownByBucket = useMemo(() => {
    if (!buckets || !data?.teamBreakdownByWeek) return null;
    const result = {};
    for (const k of buckets.keys) result[k] = { cameraman: 0, cam_regie: 0, groot: 0 };
    for (const [wk, breakdown] of Object.entries(data.teamBreakdownByWeek)) {
      const k = buckets.groupKey(wk);
      if (result[k]) {
        result[k].cameraman += breakdown.cameraman || 0;
        result[k].cam_regie += breakdown.cam_regie || 0;
        result[k].groot += breakdown.groot || 0;
      }
    }
    return result;
  }, [data, buckets]);

  function heatColor(v) {
    if (v === 0 || !maxValue) return { bg: "#f9fafb", color: "#cbd5e1" };
    const intensity = Math.min(1, v / maxValue);
    // Paars schaal: lichter → donkerder
    const opacity = 0.15 + intensity * 0.75;
    return {
      bg: `rgba(109, 40, 217, ${opacity})`,
      color: intensity > 0.5 ? "#fff" : "#1e1b4b",
    };
  }

  return (
    <Paper sx={{ p: { xs: 2, md: 3 }, mb: 3, borderRadius: 3, border: "1px solid #e5e7eb" }}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2, flexWrap: "wrap" }}>
        <HistoryIcon sx={{ color: "#6d28d9" }} />
        <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
          Geschiedenis — draaidagen per crewlid
        </Typography>
        <Box sx={{ flex: 1 }} />
        <TextField
          select
          size="small"
          value={range}
          onChange={(e) => setRange(e.target.value)}
          sx={{ minWidth: 160 }}
        >
          <MenuItem value="3m">Afgelopen 3 maanden</MenuItem>
          <MenuItem value="6m">Afgelopen 6 maanden</MenuItem>
          <MenuItem value="12m">Afgelopen 12 maanden</MenuItem>
          <MenuItem value="24m">Afgelopen 24 maanden</MenuItem>
        </TextField>
      </Stack>

      {loading && (
        <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
          <CircularProgress size={20} />
        </Box>
      )}

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {!loading && !error && data && buckets && (
        <>
          <Typography variant="caption" color="text.secondary" sx={{ display: "block", mb: 1 }}>
            {data.persons?.length || 0} crewleden • {data.from} → {data.to} •
            <strong> Granulariteit:</strong> {buckets.granularity === "month" ? "per maand" : "per week"} •
            <strong> Cache:</strong> {data.cachedAt ? new Date(data.cachedAt).toLocaleTimeString("nl-NL") : "live"}
          </Typography>

          {personRows.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              Geen historische draaidagen in dit bereik.
            </Typography>
          ) : (
            <Box sx={{ overflowX: "auto" }}>
              <Box sx={{
                display: "grid",
                gridTemplateColumns: `200px repeat(${buckets.keys.length}, minmax(28px, 1fr)) 60px`,
                gap: 0.25, alignItems: "center", minWidth: 700,
              }}>
                {/* Header row */}
                <Box />
                {buckets.keys.map((k) => (
                  <Typography
                    key={k}
                    variant="caption"
                    sx={{ fontSize: 9, color: "text.secondary", textAlign: "center" }}
                  >
                    {buckets.labelFor(k)}
                  </Typography>
                ))}
                <Typography variant="caption" sx={{ fontWeight: 600, color: "text.secondary", textAlign: "right" }}>
                  Totaal
                </Typography>

                {/* Person rows */}
                {personRows.slice(0, 25).map((p) => (
                  <React.Fragment key={p.name}>
                    <Typography variant="body2" sx={{ fontSize: 12, fontWeight: 500, pr: 1, py: 0.25, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
                      {p.name}
                    </Typography>
                    {buckets.keys.map((k) => {
                      const v = p.byBucket[k] || 0;
                      const { bg, color } = heatColor(v);
                      return (
                        <Tooltip
                          key={k}
                          title={`${p.name} • ${buckets.labelFor(k)} • ${v} draaidag${v === 1 ? "" : "en"}`}
                          arrow
                        >
                          <Box
                            sx={{
                              bgcolor: bg, color,
                              minHeight: 22, fontSize: 10, fontWeight: 600,
                              borderRadius: 0.5,
                              display: "flex", alignItems: "center", justifyContent: "center",
                              cursor: "help",
                            }}
                          >
                            {v > 0 ? v : ""}
                          </Box>
                        </Tooltip>
                      );
                    })}
                    <Typography variant="caption" sx={{ fontWeight: 700, textAlign: "right", color: "#6d28d9" }}>
                      {p.totalDraaidagen}
                    </Typography>
                  </React.Fragment>
                ))}
                {/* Team-breakdown — drie categorie-rijen, geen dubbel-tellen, stagiairs eruit */}
                {teamBreakdownByBucket && (() => {
                  const categories = [
                    { key: "cameraman", label: "🎥 Camera", color: "#0d9488", bg: "#ccfbf1" },
                    { key: "cam_regie", label: "🎬 Cam + Regie", color: "#6d28d9", bg: "#ede9fe" },
                    { key: "groot", label: "👥 Groot (3+)", color: "#be185d", bg: "#fce7f3" },
                  ];
                  return (
                    <>
                      <Box sx={{ gridColumn: `1 / span ${buckets.keys.length + 2}`, height: 8 }} />
                      {categories.map((cat, idx) => (
                        <React.Fragment key={cat.key}>
                          <Typography variant="caption" sx={{
                            fontSize: 10, fontWeight: 700, color: cat.color, pr: 1, py: 0.4,
                            borderTop: idx === 0 ? "2px solid #ddd6fe" : "1px solid #f1f5f9",
                          }}>
                            {cat.label}
                          </Typography>
                          {buckets.keys.map((k) => {
                            const v = teamBreakdownByBucket[k]?.[cat.key] || 0;
                            return (
                              <Tooltip
                                key={k}
                                title={`${buckets.labelFor(k)}: ${v} ${cat.label.replace(/^[^\s]+ /, "")} draaidag${v === 1 ? "" : "en"}`}
                                arrow
                              >
                                <Box sx={{
                                  bgcolor: v > 0 ? cat.bg : "#f9fafb",
                                  color: v > 0 ? cat.color : "#cbd5e1",
                                  minHeight: 22, fontSize: 11, fontWeight: 700,
                                  borderRadius: 0.5,
                                  display: "flex", alignItems: "center", justifyContent: "center",
                                  cursor: "help",
                                  borderTop: idx === 0 ? "2px solid #ddd6fe" : "1px solid #f1f5f9",
                                }}>
                                  {v > 0 ? v : ""}
                                </Box>
                              </Tooltip>
                            );
                          })}
                          <Typography variant="caption" sx={{
                            fontWeight: 800, textAlign: "right", color: cat.color, py: 0.4,
                            borderTop: idx === 0 ? "2px solid #ddd6fe" : "1px solid #f1f5f9",
                          }}>
                            {Object.values(teamBreakdownByBucket).reduce((acc, b) => acc + (b[cat.key] || 0), 0)}
                          </Typography>
                        </React.Fragment>
                      ))}
                    </>
                  );
                })()}
              </Box>
              {personRows.length > 25 && (
                <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>
                  + {personRows.length - 25} meer crewleden niet getoond. Sorteer op aantal draaidagen.
                </Typography>
              )}
            </Box>
          )}
        </>
      )}
    </Paper>
  );
}

